import type {Anchor, CellAddress, ItemId} from '../types'
import {cellPosition, columnPagingPosition, isSamePosition, itemPagingPosition, rowPagingPosition} from './positions'
import type {
  BoardFocusAction,
  BoardFocusAnchor,
  BoardFocusAxis,
  BoardFocusFollow,
  BoardFocusGrid,
  BoardFocusKind,
  BoardFocusPosition,
  BoardFocusState,
  BoardFocusStep,
} from './types'

/**
 * Moving focus around a board, as a reducer over a position in the data.
 *
 * ## What was taken from the Projects board, and what was not
 *
 * `packages/memex/src/client/navigation` and `components/board/navigation.ts` have solved
 * this shape for years, on a board that already has swimlanes. Nothing is imported from
 * there and no file was copied in: it is their code, on their release cycle, shaped by
 * their product. What was reused is the reasoning, and it is worth naming which parts.
 *
 * **Taken.** Focus is a coordinate in the data rather than an element, so the model
 * survives the element not existing. Movement is expressed as a direction along an axis
 * rather than as a key, so the reducer never sees an event and the key map is somebody
 * else's problem. Directions clamp at the ends instead of wrapping. And the split that
 * makes a position durable: the coordinate is held as identities, while indexes are
 * remembered separately and read only when an identity has gone, which is how a position
 * survives its column or its card being deleted underneath it.
 *
 * **Not taken.** Their `FocusType` (read, edit, suspended) belongs to a surface with
 * in-place editing and has no meaning here. Their reducer takes a feature-flag map, which
 * this one must not. Their navigation skips a column with no cards, because on that board
 * an empty column is not a stop; here every column-and-row intersection is a real cell
 * and a keyboard coordinate, so an empty cell is somewhere focus lands. Their factory
 * builds a navigation context generic over any focus union, which is worth it across two
 * live callers and is not worth it for one board. And their card-move state, which is
 * https://github.com/github/views/issues/46 rather than this.
 *
 * ## Positions that are not cells
 *
 * A board has three places focus goes that are not a column-and-row pair, one per paging
 * direction. None of them is a control anybody presses, so none takes a tab stop of its
 * own; each is a coordinate this reducer can name, and the board's one roving stop lands on
 * whatever is drawing it. `positions.ts` says which half of the address each one occupies
 * and which half the board carries for it.
 *
 * A swimlane's name is deliberately not one of them. It is not interactive, and the cell a
 * reader arrives in is already named after the swimlane it is in, so a stop there would add
 * a position a caller has to draw and buy nothing a reader does not already hear.
 */
export function boardFocusReducer(state: BoardFocusState, action: BoardFocusAction): BoardFocusState {
  switch (action.type) {
    case 'move': {
      const from = resolve(action.grid, state)
      if (from === null) return settleEmpty(state)

      return settle(state, stepFrom(action.grid, from, action.axis, action.step), true, state.hasFocus, null)
    }
    case 'focus': {
      const placed = place(action.grid, action.position, state)
      // A position naming a column or row this board does not hold is not a board
      // coordinate, so there is nothing to move to and nothing to report.
      return placed === null ? state : settle(state, placed, true, state.hasFocus, null, action.byPointer ?? false)
    }
    case 'observe': {
      const placed = place(action.grid, action.position, state)
      // Focus coming back to the card being followed is the menu that moved it closing.
      const following = isSamePosition(state.position, action.position) ? state.following : null
      // An element said focus arrived at it, so focus really is inside the board now,
      // however the position came to be where it is.
      return placed === null ? state : settle(state, placed, false, true, following)
    }
    case 'release': {
      const following = action.toNowhere === true ? state.following : null

      return state.pendingFocus || state.hasFocus || following !== state.following
        ? {...state, pendingFocus: false, isPointerFocus: false, hasFocus: false, following}
        : state
    }
    case 'stray': {
      const following = action.toRoot === true ? state.following : null
      // A pointer's request only puts focus back where a drop left nowhere, so focus the
      // reader put somewhere else in the board withdraws it.
      if (state.isPointerFocus) {
        return {...state, pendingFocus: false, isPointerFocus: false, hasFocus: false, following}
      }

      return state.hasFocus || following !== state.following ? {...state, hasFocus: false, following} : state
    }
    case 'follow': {
      const {grid, card, to, anchor} = action
      if (!isSamePosition(state.position, cellPosition(card, card.itemId))) return state

      const from = {columnKey: card.columnKey, rowKey: card.rowKey}
      const alsoAt = cellsHolding(grid, card.itemId).filter(cell => !isSameCell(cell, from))
      const following: BoardFocusFollow = {itemId: card.itemId, from, to, fromIndex: state.anchor.itemIndex, alsoAt}

      return {...state, following: anchor === undefined ? following : {...following, anchor}}
    }
    case 'unfollow':
      return state.following === null ? state : {...state, following: null}
    case 'reconcile': {
      const arrived = state.following === null ? undefined : arrivalOf(action.grid, state.following)
      // A card the reader moved from inside it has landed, so focus goes after it.
      if (arrived) return settle(state, arrived, true, state.hasFocus, null)

      const following = arrived === undefined ? state.following : null
      const resolved = resolve(action.grid, state)
      if (resolved === null) return settleEmpty(state)

      /*
        New data moved the position out from under a reader who was standing on it: the
        cell finished paging and its ghost is gone, the card was closed, the swimlane
        stopped being named. The element they were on has unmounted, so focus has already
        fallen to the document, and the repaired position is somewhere no element has been
        asked to take focus.

        So focus is asked for again, exactly as a move asks for it, and the element at the
        repaired position takes it. Only when the position really moved and only when focus
        was inside the board: a reader who was not on the board is not pulled onto it, and
        a repair that changed nothing is not a reason to take focus back off whatever the
        reader has since moved to themselves.
      */
      const hasMoved = !isSamePosition(state.position, resolved.position)
      const pendingFocus = state.pendingFocus || (state.hasFocus && hasMoved)

      return settle(state, resolved, pendingFocus, state.hasFocus, following, state.isPointerFocus)
    }
  }
}

const NOWHERE: BoardFocusAnchor = {columnIndex: 0, rowIndex: 0, itemIndex: -1}

/**
 * The state of a board nothing has been said about yet. Not exported: a board always has
 * a grid to seed from, so callers use `initialBoardFocusState`.
 */
const UNSEEDED: BoardFocusState = {
  position: null,
  anchor: NOWHERE,
  pendingFocus: false,
  isPointerFocus: false,
  hasFocus: false,
  following: null,
}

/**
 * The first position on a board, which is the first card of the first cell, or that cell
 * itself when it holds nothing.
 *
 * Always a cell, whatever else the board has: the roving tabindex needs an item to be the
 * board's tab stop from the start, and the first card of the first cell is the one every
 * window draws. A paging ghost or a row header would be neither.
 *
 * Seeded before anything is rendered rather than on the first keypress. Seeding sets
 * nothing pending and nothing focused, so a board that has just mounted does not take
 * focus from the page around it, and does not report a card nobody has touched.
 */
export function initialBoardFocusState(grid: BoardFocusGrid): BoardFocusState {
  return boardFocusReducer(UNSEEDED, {type: 'reconcile', grid})
}

export {isSamePosition} from './positions'

/** A position and the indexes it sat at, which is everything the state holds but pending. */
type ResolvedPosition = {
  position: BoardFocusPosition
  anchor: BoardFocusAnchor
}

/**
 * Returns the state unchanged when nothing about it moved, so `useReducer` bails out of
 * the render rather than re-rendering every card on the board to arrive where it was.
 *
 * The anchor is compared as well as the position, which is what makes a carried half
 * changing a change: two row-header positions on the same swimlane are the same place, and
 * the reader having walked there from a different column is still worth settling.
 */
function settle(
  state: BoardFocusState,
  resolved: ResolvedPosition,
  pendingFocus: boolean,
  hasFocus: boolean,
  following: BoardFocusFollow | null,
  isPointerFocus = false,
): BoardFocusState {
  const {position, anchor} = resolved
  const isPointerRequest = pendingFocus && isPointerFocus
  const unchanged =
    state.pendingFocus === pendingFocus &&
    state.isPointerFocus === isPointerRequest &&
    state.hasFocus === hasFocus &&
    state.following === following &&
    isSamePosition(state.position, position) &&
    state.anchor.columnIndex === anchor.columnIndex &&
    state.anchor.rowIndex === anchor.rowIndex &&
    state.anchor.itemIndex === anchor.itemIndex

  return unchanged ? state : {position, anchor, pendingFocus, isPointerFocus: isPointerRequest, hasFocus, following}
}

/**
 * A board with no columns or no rows has nowhere to put focus, and says so rather than
 * pointing at a key it no longer holds.
 *
 * Nothing is focused either. There is no position left for focus to be at, so a board that
 * has just lost its last column must not go on reporting that somebody is on a card.
 */
function settleEmpty(state: BoardFocusState): BoardFocusState {
  return state.position === null && !state.pendingFocus && !state.hasFocus && state.following === null
    ? state
    : {...state, position: null, pendingFocus: false, isPointerFocus: false, hasFocus: false, following: null}
}

/**
 * Where a followed card has landed, `undefined` while the data still has it where it was,
 * and `null` when the data put it somewhere other than where it was sent.
 *
 * A card moved to another cell lands on the card where that cell holds it, and on the cell
 * itself where the cell has cards still to load, which is a card sorted into a page nobody
 * has loaded. A card reordered in its own cell lands once it sits where the move anchored
 * it. Focus goes where the reader sent the card rather than to whatever is left behind.
 */
function arrivalOf(grid: BoardFocusGrid, following: BoardFocusFollow): ResolvedPosition | null | undefined {
  const {itemId, from, to, anchor, fromIndex} = following
  const fromIds = grid.itemIdsAt(from)
  const indexInFrom = fromIds.indexOf(itemId)

  if (isSameCell(from, to)) {
    if (indexInFrom === -1 || anchor === undefined) return null
    if (sitsAt(fromIds, indexInFrom, anchor)) {
      return atCell(grid, grid.columnKeys.indexOf(from.columnKey), grid.rowKeys.indexOf(from.rowKey), indexInFrom)
    }

    // Unchanged is a move still on its way. Anywhere else in the cell is somebody else's.
    return indexInFrom === fromIndex ? undefined : null
  }

  if (indexInFrom !== -1) return undefined

  const columnIndex = grid.columnKeys.indexOf(to.columnKey)
  const rowIndex = grid.rowKeys.indexOf(to.rowKey)
  if (columnIndex === -1 || rowIndex === -1) return null

  const indexInTo = grid.itemIdsAt(to).indexOf(itemId)
  if (indexInTo !== -1) return atCell(grid, columnIndex, rowIndex, indexInTo)
  if (!grid.hasItemPagingAt(to) || isLoadedElsewhere(grid, following)) return null

  return atCell(grid, columnIndex, rowIndex, -1)
}

function isSameCell(a: CellAddress, b: CellAddress): boolean {
  return a.columnKey === b.columnKey && a.rowKey === b.rowKey
}

/** Whether the card at `index` sits where `anchor` puts it. */
function sitsAt(itemIds: readonly ItemId[], index: number, anchor: Anchor): boolean {
  if ('before' in anchor) return itemIds[index + 1] === anchor.before
  if ('after' in anchor) return itemIds[index - 1] === anchor.after

  return anchor.at === 'start' ? index === 0 : index === itemIds.length - 1
}

/**
 * Whether a cell other than `to` has loaded the card, leaving out the cells that already
 * held it when it was moved. Scans every cell, so it is asked only once, for a card that
 * left its cell and is not among the cards `to` has loaded.
 */
function isLoadedElsewhere(grid: BoardFocusGrid, {itemId, to, alsoAt}: BoardFocusFollow): boolean {
  return cellsHolding(grid, itemId).some(
    cell => !isSameCell(cell, to) && !alsoAt.some(heldBefore => isSameCell(heldBefore, cell)),
  )
}

function cellsHolding(grid: BoardFocusGrid, itemId: ItemId): CellAddress[] {
  return grid.columnKeys.flatMap(columnKey =>
    grid.rowKeys.flatMap(rowKey => (grid.itemIdsAt({columnKey, rowKey}).includes(itemId) ? [{columnKey, rowKey}] : [])),
  )
}

/**
 * Where the current position sits on this board, repairing it where the board has moved
 * underneath it.
 *
 * A key that is still there wins outright, because that is the card or column the person
 * is on however far it has moved. A key that has gone falls back to the index it was last
 * seen at, clamped into what the axis holds now, which lands focus beside where the
 * missing thing was rather than back at the start of the board.
 */
function resolve(grid: BoardFocusGrid, state: BoardFocusState): ResolvedPosition | null {
  const {columnKeys, rowKeys} = grid
  const previous = state.position

  const columnIndex = previous === null ? 0 : indexOf(columnKeys, previous.columnKey, state.anchor.columnIndex)
  const rowIndex = previous === null ? 0 : indexOf(rowKeys, previous.rowKey, state.anchor.rowIndex)
  const columnKey = columnKeys[columnIndex]
  const rowKey = rowKeys[rowIndex]
  if (columnKey === undefined || rowKey === undefined) return null

  if (previous !== null && previous.kind !== 'cell') {
    return resolveElsewhere(grid, previous.kind, columnIndex, rowIndex)
  }

  const itemIds = grid.itemIdsAt({columnKey, rowKey})

  let itemIndex: number
  if (previous === null) {
    // Seeding. The first card of the cell, or the cell itself when it holds none.
    itemIndex = itemIds.length === 0 ? -1 : 0
  } else if (previous.itemId === null) {
    // Focus was on the cell rather than on a card in it, and a cell that has since been
    // given cards must not pull focus onto one of them.
    itemIndex = -1
  } else {
    itemIndex = indexOf(itemIds, previous.itemId, state.anchor.itemIndex)
  }

  return atCell(grid, columnIndex, rowIndex, itemIndex)
}

/**
 * The repair for the four positions that are not cells.
 *
 * Each is held by a fact the caller states, and each falls back to the cell it was beside
 * when that fact stops being true: a cell that has finished paging, an axis that has run
 * out. Beside it rather than at the start of the board, for the same reason a deleted card
 * falls back to its neighbour.
 */
function resolveElsewhere(
  grid: BoardFocusGrid,
  kind: Exclude<BoardFocusKind, 'cell'>,
  columnIndex: number,
  rowIndex: number,
): ResolvedPosition | null {
  switch (kind) {
    case 'itemPaging':
      return atItemPaging(grid, columnIndex, rowIndex) ?? endOfCards(grid, columnIndex, rowIndex)
    case 'columnPaging':
      // Re-pinned to whichever column is the last one now, because the ghost is past the
      // axis rather than at a column, and the axis has since grown or run out.
      return atColumnPaging(grid, rowIndex) ?? atCell(grid, grid.columnKeys.length - 1, rowIndex, 0)
    case 'rowPaging':
      return atRowPaging(grid, columnIndex) ?? endOfCellStack(grid, columnIndex, grid.rowKeys.length - 1)
  }
}

/**
 * Where a stated position sits on this board, or `null` when this board does not hold it.
 *
 * A card the board does not hold, in a cell it does, resolves to the cell. That is not
 * the same as failing: the pair is a real coordinate, and it is where a card that has
 * just been closed used to be.
 *
 * The half of the address a position does not occupy is taken from where the reader
 * already is rather than from what was stated, because the element drawing a row header or
 * a paging ghost is one element for a whole swimlane or a whole board and cannot know it.
 */
function place(grid: BoardFocusGrid, position: BoardFocusPosition, state: BoardFocusState): ResolvedPosition | null {
  switch (position.kind) {
    case 'cell': {
      const columnIndex = grid.columnKeys.indexOf(position.columnKey)
      const rowIndex = grid.rowKeys.indexOf(position.rowKey)
      if (columnIndex === -1 || rowIndex === -1) return null

      const itemIds = grid.itemIdsAt(position)
      const itemIndex = position.itemId === null ? -1 : itemIds.indexOf(position.itemId)

      return atCell(grid, columnIndex, rowIndex, itemIndex)
    }
    case 'itemPaging': {
      const columnIndex = grid.columnKeys.indexOf(position.columnKey)
      const rowIndex = grid.rowKeys.indexOf(position.rowKey)
      if (columnIndex === -1 || rowIndex === -1) return null

      return atItemPaging(grid, columnIndex, rowIndex)
    }
    case 'columnPaging':
      return atColumnPaging(grid, statedRowIndex(grid, state, position.rowKey))
    case 'rowPaging':
      return atRowPaging(grid, statedColumnIndex(grid, state, position.columnKey))
  }
}

function stepFrom(
  grid: BoardFocusGrid,
  from: ResolvedPosition,
  axis: BoardFocusAxis,
  step: BoardFocusStep,
): ResolvedPosition {
  switch (axis) {
    case 'column':
      return stepColumn(grid, from, step)
    case 'row':
      return stepRow(grid, from, step)
    case 'card':
      return stepCard(grid, from, step)
  }
}

/**
 * Sideways, keeping the row, and one step further than the columns: past the last one is
 * the ghost saying there are more.
 *
 * The row axis's ghost spans the board's width, so there is nothing beside it and the key
 * is left alone to scroll. `first` and `last` are the ends of the columns rather than of
 * the axis, because reaching the end of a row is a statement about the columns and a ghost
 * is not one of them.
 */
function stepColumn(grid: BoardFocusGrid, from: ResolvedPosition, step: BoardFocusStep): ResolvedPosition {
  const {kind} = from.position
  if (kind === 'rowPaging') return from

  const {columnKeys} = grid
  const {columnIndex, rowIndex, itemIndex} = from.anchor

  if (step === 'first' || step === 'last') {
    const endIndex = stepIndex(columnKeys.length, columnIndex, step)
    if (kind === 'cell' && endIndex === columnIndex) return from

    return atCell(grid, endIndex, rowIndex, carriedItemIndex(itemIndex)) ?? from
  }

  const pagingIndex = grid.hasColumnPaging ? columnKeys.length : -1
  const fromIndex = kind === 'columnPaging' ? columnKeys.length : columnIndex
  const wantedIndex = stepIndex(columnKeys.length + (grid.hasColumnPaging ? 1 : 0), fromIndex, step)
  // Clamped at the end of the axis, so stay put rather than rebuilding the position that
  // is already held. The carried card index is what makes the difference: in the cell
  // focus is already in, it would answer a press against an edge by moving off a cell that
  // has since been given cards and onto the first of them.
  if (wantedIndex === fromIndex) return from
  if (wantedIndex === pagingIndex) return atColumnPaging(grid, rowIndex) ?? from

  return atCell(grid, wantedIndex, rowIndex, carriedItemIndex(itemIndex)) ?? from
}

/**
 * Between swimlanes, keeping the column, and one step further than the rows: below the
 * last one is the ghost saying there are more.
 *
 * From a row header it moves between row headers, because a reader skimming swimlane names
 * is asking for the next name rather than for the cell underneath it. The column axis's
 * ghost is in the sticky strip above every row, so there is nothing above or below it.
 */
function stepRow(grid: BoardFocusGrid, from: ResolvedPosition, step: BoardFocusStep): ResolvedPosition {
  const {kind} = from.position
  if (kind === 'columnPaging') return from

  const {rowKeys} = grid
  const {columnIndex, rowIndex, itemIndex} = from.anchor

  if (step === 'first' || step === 'last') {
    const endIndex = stepIndex(rowKeys.length, rowIndex, step)
    if (kind === 'cell' && endIndex === rowIndex) return from

    return atCell(grid, columnIndex, endIndex, carriedItemIndex(itemIndex)) ?? from
  }

  const pagingIndex = grid.hasRowPaging ? rowKeys.length : -1
  const fromIndex = kind === 'rowPaging' ? rowKeys.length : rowIndex
  const wantedIndex = stepIndex(rowKeys.length + (grid.hasRowPaging ? 1 : 0), fromIndex, step)
  if (wantedIndex === fromIndex) return from
  if (wantedIndex === pagingIndex) return atRowPaging(grid, columnIndex) ?? from

  return atCell(grid, columnIndex, wantedIndex, carriedItemIndex(itemIndex)) ?? from
}

/**
 * The board's vertical reading order, which is more than one cell's cards.
 *
 * Down a cell's cards, then that cell's own paging ghost when it has one, then the top of
 * the cell beneath, and past the last swimlane the ghost saying there are more rows. Up is
 * the same list backwards. That spill is what makes this the board's vertical axis rather
 * than a list's, and on a board with one row and no paging there is nothing on either side
 * of a cell's cards, so it clamps exactly as it always did.
 *
 * `first` and `last` do not spill, because they are a statement about this cell's cards.
 * From a ghost they are the same statement about the cell it belongs to.
 */
function stepCard(grid: BoardFocusGrid, from: ResolvedPosition, step: BoardFocusStep): ResolvedPosition {
  const {columnIndex, rowIndex, itemIndex} = from.anchor

  if (step === 'first' || step === 'last') {
    const itemIds = itemIdsAt(grid, columnIndex, rowIndex)

    return atCell(grid, columnIndex, rowIndex, stepIndex(itemIds.length, itemIndex, step)) ?? from
  }

  switch (from.position.kind) {
    case 'columnPaging':
      // In the sticky strip past the last column, with no swimlane above or below it.
      return from
    case 'rowPaging':
      return step === 'next' ? from : (endOfCellStack(grid, columnIndex, grid.rowKeys.length - 1) ?? from)
    case 'itemPaging':
      return step === 'previous'
        ? (endOfCards(grid, columnIndex, rowIndex) ?? from)
        : (afterRow(grid, columnIndex, rowIndex) ?? from)
    case 'cell': {
      const itemIds = grid.itemIdsAt(from.position)
      const wantedIndex = step === 'next' ? itemIndex + 1 : itemIndex - 1
      if (wantedIndex >= 0 && wantedIndex < itemIds.length) {
        return atCell(grid, columnIndex, rowIndex, wantedIndex) ?? from
      }

      // `wantedIndex` is not clamped and used: from a cell that has since been given cards
      // it is two before the first card, and clamping that would answer a press upwards by
      // moving focus down onto one.
      if (step === 'next') {
        return atItemPaging(grid, columnIndex, rowIndex) ?? afterRow(grid, columnIndex, rowIndex) ?? from
      }

      return beforeRow(grid, columnIndex, rowIndex) ?? from
    }
  }
}

/**
 * What is below everything this row holds in this column: the top of the cell in the
 * swimlane beneath, and past the last swimlane the row axis's ghost.
 *
 * `null` at the true end of the board, which is what leaves the key alone so it scrolls.
 */
function afterRow(grid: BoardFocusGrid, columnIndex: number, rowIndex: number): ResolvedPosition | null {
  if (rowIndex >= grid.rowKeys.length - 1) return atRowPaging(grid, columnIndex)

  return atCell(grid, columnIndex, rowIndex + 1, 0)
}

/** What is above everything this row holds in this column, which is the end of the row before it. */
function beforeRow(grid: BoardFocusGrid, columnIndex: number, rowIndex: number): ResolvedPosition | null {
  if (rowIndex <= 0) return null

  return endOfCellStack(grid, columnIndex, rowIndex - 1)
}

/** The last thing in one cell: its paging ghost when it has one, and its last card when it does not. */
function endOfCellStack(grid: BoardFocusGrid, columnIndex: number, rowIndex: number): ResolvedPosition | null {
  return atItemPaging(grid, columnIndex, rowIndex) ?? endOfCards(grid, columnIndex, rowIndex)
}

/** The last card in one cell, or the cell itself when it holds none. */
function endOfCards(grid: BoardFocusGrid, columnIndex: number, rowIndex: number): ResolvedPosition | null {
  return atCell(grid, columnIndex, rowIndex, itemIdsAt(grid, columnIndex, rowIndex).length - 1)
}

/**
 * Builds a cell position from three indexes, clamping the card into what the cell it lands
 * in actually holds. `null` only when an axis index has nothing behind it, which is a board
 * with no columns or no rows.
 */
function atCell(
  grid: BoardFocusGrid,
  columnIndex: number,
  rowIndex: number,
  itemIndex: number,
): ResolvedPosition | null {
  const columnKey = grid.columnKeys[columnIndex]
  const rowKey = grid.rowKeys[rowIndex]
  if (columnKey === undefined || rowKey === undefined) return null

  const itemIds = grid.itemIdsAt({columnKey, rowKey})
  const settledIndex = itemIndex < 0 ? -1 : clamp(itemIndex, itemIds.length)
  const itemId = settledIndex === -1 ? null : (itemIds[settledIndex] ?? null)

  return {
    position: cellPosition({columnKey, rowKey}, itemId),
    anchor: {columnIndex, rowIndex, itemIndex: itemId === null ? -1 : settledIndex},
  }
}

/** `null` when this cell draws no ghost, which is its caller stating it holds everything. */
function atItemPaging(grid: BoardFocusGrid, columnIndex: number, rowIndex: number): ResolvedPosition | null {
  const columnKey = grid.columnKeys[columnIndex]
  const rowKey = grid.rowKeys[rowIndex]
  if (columnKey === undefined || rowKey === undefined) return null
  if (!grid.hasItemPagingAt({columnKey, rowKey})) return null

  return {position: itemPagingPosition({columnKey, rowKey}), anchor: anchorAt(columnIndex, rowIndex)}
}

/** Past the last column, in the row the reader is in. */
function atColumnPaging(grid: BoardFocusGrid, rowIndex: number): ResolvedPosition | null {
  if (!grid.hasColumnPaging) return null

  const columnIndex = grid.columnKeys.length - 1
  const columnKey = grid.columnKeys[columnIndex]
  const rowKey = grid.rowKeys[rowIndex]
  if (columnKey === undefined || rowKey === undefined) return null

  return {position: columnPagingPosition(columnKey, rowKey), anchor: anchorAt(columnIndex, rowIndex)}
}

/** Below the last swimlane, in the column the reader is in. */
function atRowPaging(grid: BoardFocusGrid, columnIndex: number): ResolvedPosition | null {
  if (!grid.hasRowPaging) return null

  const rowIndex = grid.rowKeys.length - 1
  const columnKey = grid.columnKeys[columnIndex]
  const rowKey = grid.rowKeys[rowIndex]
  if (columnKey === undefined || rowKey === undefined) return null

  return {position: rowPagingPosition(columnKey, rowKey), anchor: anchorAt(columnIndex, rowIndex)}
}

/** Nothing that is not a cell is on a card, so every one of them remembers no card index. */
function anchorAt(columnIndex: number, rowIndex: number): BoardFocusAnchor {
  return {columnIndex, rowIndex, itemIndex: -1}
}

function itemIdsAt(grid: BoardFocusGrid, columnIndex: number, rowIndex: number): readonly string[] {
  const columnKey = grid.columnKeys[columnIndex]
  const rowKey = grid.rowKeys[rowIndex]
  if (columnKey === undefined || rowKey === undefined) return []

  return grid.itemIdsAt({columnKey, rowKey})
}

/**
 * The column a stated position meant, for a kind that does not occupy one.
 *
 * A position read back from `useBoardFocus` carries the real key, so handing one straight
 * back puts the reader where they were. An element drawing a row header or a ghost cannot
 * know the column and says so with a key this board does not hold, and that falls back to
 * wherever the reader already is.
 */
function statedColumnIndex(grid: BoardFocusGrid, state: BoardFocusState, statedKey: string): number {
  const stated = grid.columnKeys.indexOf(statedKey)

  return stated === -1 ? carriedColumnIndex(grid, state) : stated
}

/** The same, for the row half. */
function statedRowIndex(grid: BoardFocusGrid, state: BoardFocusState, statedKey: string): number {
  const stated = grid.rowKeys.indexOf(statedKey)

  return stated === -1 ? carriedRowIndex(grid, state) : stated
}

/** The column the reader is in, for a position that spans a swimlane rather than sitting in one. */
function carriedColumnIndex(grid: BoardFocusGrid, state: BoardFocusState): number {
  return state.position === null ? 0 : indexOf(grid.columnKeys, state.position.columnKey, state.anchor.columnIndex)
}

/** The row the reader is in, for a position that spans the board rather than sitting in one. */
function carriedRowIndex(grid: BoardFocusGrid, state: BoardFocusState): number {
  return state.position === null ? 0 : indexOf(grid.rowKeys, state.position.rowKey, state.anchor.rowIndex)
}

/**
 * The card index a move across the board carries with it. Focus sitting anywhere but on a
 * card carries the top of the cell, so moving out of an empty cell, off a row header or off
 * a ghost lands on the first card of the cell it arrives in rather than on nothing.
 */
function carriedItemIndex(itemIndex: number): number {
  return Math.max(itemIndex, 0)
}

function stepIndex(length: number, current: number, step: BoardFocusStep): number {
  if (length === 0) return -1

  switch (step) {
    case 'first':
      return 0
    case 'last':
      return length - 1
    case 'next':
      return Math.min(current + 1, length - 1)
    case 'previous':
      return Math.max(current - 1, 0)
  }
}

function clamp(index: number, length: number): number {
  if (length === 0) return -1

  return Math.min(Math.max(index, 0), length - 1)
}

/** The key's own index, or the remembered one clamped into what is left when it has gone. */
function indexOf(keys: readonly string[], key: string, remembered: number): number {
  const found = keys.indexOf(key)

  return found === -1 ? clamp(remembered, keys.length) : found
}
